/**
 * Insert a Cloudinary transformation into an existing secure delivery URL.
 * Works for both the raw cloudinary.com host and a CDN rewrite that keeps the
 * `/image/upload/` path segment.
 *
 * Example:
 *   cloudinaryTransform(
 *     "https://res.cloudinary.com/demo/image/upload/v123/foo.jpg",
 *     "w_240,h_240,c_fill,q_auto,f_auto"
 *   )
 *   // => "https://res.cloudinary.com/demo/image/upload/w_240,h_240,c_fill,q_auto,f_auto/v123/foo.jpg"
 */
export function cloudinaryTransform(url, transform) {
  if (!url || typeof url !== "string") return url;
  if (!transform) return url;
  const marker = "/upload/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const head = url.slice(0, idx + marker.length);
  const tail = url.slice(idx + marker.length);
  return `${head}${transform}/${tail}`;
}

export const PORTFOLIO_THUMB_TRANSFORM = "w_240,h_240,c_fill,q_auto,f_auto";
export const PORTFOLIO_EDIT_THUMB_TRANSFORM = "w_120,h_120,c_fill,q_auto,f_auto";
export const PORTFOLIO_VIDEO_POSTER_TRANSFORM = "w_240,h_240,c_fill,q_auto,so_auto";
// Wide-aspect crop for the portfolio card preview (a full-width, 140px-tall
// box) — matching the card's own aspect ratio here, rather than reusing the
// square PORTFOLIO_THUMB_TRANSFORM, avoids double-cropping (a square crop
// stretched to fill a wide box) that made the image look zoomed-in and blurry.
export const PORTFOLIO_CARD_TRANSFORM = "w_600,h_280,c_fill,g_auto,q_auto,f_auto";
export const PORTFOLIO_CARD_VIDEO_POSTER_TRANSFORM = "w_600,h_280,c_fill,g_auto,q_auto,so_auto";

/**
 * Build a poster-frame (thumbnail) URL for a Cloudinary video delivery URL.
 * Cloudinary generates a still frame when a video is requested with an image
 * extension, so this inserts the transform after `/upload/` and swaps the
 * video extension (e.g. `.mp4`) for `.jpg`, preserving any query string.
 *
 * Example:
 *   cloudinaryVideoPoster(
 *     "https://res.cloudinary.com/demo/video/upload/v1/portfolio/videos/x/y.mp4",
 *     PORTFOLIO_VIDEO_POSTER_TRANSFORM
 *   )
 *   // => "https://res.cloudinary.com/demo/video/upload/w_240,h_240,c_fill,q_auto,so_auto/v1/portfolio/videos/x/y.jpg"
 */
export function cloudinaryVideoPoster(url, transform) {
  const withTransform = cloudinaryTransform(url, transform);
  if (!withTransform || typeof withTransform !== "string") return withTransform;
  return withTransform.replace(/\.[a-z0-9]+(\?.*)?$/i, ".jpg$1");
}
